// Note 에디터 컨트롤러 — editor_core.js의 buildTools/createEditorInstance를 사용
//
// 도구 설정, EditorJS 인스턴스 생성, 블록 마이그레이션은 editor_core.js에서 처리.
// 이 파일은 Note 전용 로직만 담당:
//   - title/subtitle/tagList 메타데이터 관리
//   - auto-save (title + editor_data API PATCH)
//   - 미리보기 동기화
//   - AI chat 연동
//   - Note 전용 단축키 (TextBox, Columns, Footnote)
//
import { Controller } from "@hotwired/stimulus";
import api from "@/utils/api";
import { buildTools, createEditorInstance } from "@/editors/editor_core";
import { refreshFootnotePanel, destroyFootnotePanel } from '@/editorjs/tools/footnote/panel';
import { EditorHeaderToc } from '@/editorjs/header_toc';
import { CollabSession } from '@/editorjs/collab';
import { AwarenessCursor } from '@/editorjs/collab/awareness_cursor';
import { PresencePanel } from '@/editorjs/collab/presence_panel';
import { MetaBinding } from '@/editorjs/collab/meta_binding';

export default class extends Controller {
    static targets = [
        "editorForm", "editorData", "autoSaveSubmit", "autoSavedLabel", "saveAndPublish",
        "title", "subtitle", "tagList", "postCategorySlug", "warningLabel", "presencePanel",
        "previewButton", "overlay", "actionsContainer", "toc"
    ];
    static values = {
        primaryBackgroundColor: String,
        primaryTextColor: String,
        lightMode: Boolean,
        currentPlan: String
    }

    showAutoSaveError() {
        createFlash({
            key: "error",
            ttl: 5,
            message: "자동 저장에 일시적으로 실패했습니다. 잠시 후 다시 시도합니다.",
            stack: false
        });
    }

    initialize() {
        this.editor = null;
        this.undo = null;
        this.alreadySaving = false;
        this.isDragging = false;
        this.previewWindow = null;
        this.dragResetTimer = null; // 드래그 상태 리셋을 위한 타이머
        this.isTyping = false; // 타이핑 중인지 확인하는 플래그
        this.typingTimer = null; // 타이핑 종료 감지용 타이머
        this.isComposing = false; // IME 입력 중인지 확인하는 플래그
        this.sessionExpired = false; // 세션 만료 여부

        // Safari 브라우저 감지
        this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

        window.connectPreview = (previewWindowInstance) => {
            this.previewWindow = previewWindowInstance;

            // 연결되자마자 최신 내용으로 한번 새로고침 해줍니다.
            if (this.previewWindow && !this.previewWindow.closed) {
                this.previewWindow.refreshPreview();
            }
        };
    }

    submitForm() {
        if (this.sessionExpired) {
            alert("세션이 만료되어 저장할 수 없습니다. 다른 탭에서 로그인한 후 이 페이지로 돌아와 내용을 복사해주세요.");
            return;
        }

        clearInterval(this.autoSaver);

        if (this.isExceptionalEnvironment()) {
            return;
        }

        this.enableLeave();

        this.editor.save().then((outputData) => {
            this.editorDataTarget.value = JSON.stringify(outputData);
            this.editorFormTarget.submit();
        }).catch((error) => {
            alert(error.response.data.error);
        });
    }

    publishNote(e) {
        if (this.sessionExpired) {
            alert("세션이 만료되어 저장할 수 없습니다. 다른 탭에서 로그인한 후 이 페이지로 돌아와 내용을 복사해주세요.");
            return;
        }

        this.enableLeave();

        if (this.isExceptionalEnvironment()) {
            return;
        }

        this.editor.save().then((data) => {
            this.editorDataTarget.value = JSON.stringify(data);
            this.saveAndPublishTarget.click();
        });
    }

    enableLeave = () => {
        this.data.set("protect-leave", "off");
    };

    // production 에서만 이탈 방지로직 동작
    leavingPage = (event) => {
        const areYouSure = "정말 페이지를 나가시나요? 저장을 하셨는지 꼭 확인해주세요 :)";

        if ((this.data.get("protect-leave") === "on") && (process.env.NODE_ENV === "production")) {
            if (event.type === "turbo:before-visit") {
                if (!window.confirm(areYouSure)) {
                    event.preventDefault()
                }
            } else {
                event.returnValue = areYouSure;
                return event.returnValue;
            }
        }
    };

    openPreview(event) {
        event.preventDefault();

        if (!this.editor || !this.editor.isReady) {
            alert("에디터가 아직 준비 중입니다. 잠시 후 다시 시도해주세요.");
            return;
        }

        // 팝업이 이미 열려 있고, 닫히지 않았다면 새로 열지 않고 포커스만 줍니다.
        if (this.previewWindow && !this.previewWindow.closed) {
            this.previewWindow.focus();
            return;
        }

        const {previewUrl} = event.currentTarget.dataset;

        // 가장 최근 내용이 반영되도록 먼저 강제 저장
        this.autoSave();

        // iPhone 14/15 Pro 크기에 맞춘 팝업 창 옵션 (테두리, 스크롤바 등 고려하여 약간 더 크게)
        const windowFeatures = "width=800,height=1060,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes";

        this.previewWindow = window.open(previewUrl, "preview", windowFeatures);
    }

    // deepl extension : chrome-extension://cofdbpoegempjloogbagkncekinflcnj/build/content.css
    isExceptionalEnvironment = () => {
        const deeplInlineTranslator = document.querySelectorAll("deepl-inline-translate");
        if (deeplInlineTranslator.length > 0) {
            alert("Deepl 익스텐션을 사용하시면 에디터가 정상적으로 작동하지 않습니다. Deepl 익스텐션을 완전 삭제 하신 후 사용해주세요.");
            return true;
        }

        return false;
    }

    sendCurrentBlockIdToPreview() {
        // 미리보기 창이 없거나 닫혔으면 아무것도 하지 않음
        if (!this.previewWindow || this.previewWindow.closed) {
            return;
        }

        try {
            // Editor.js API를 사용해 현재 블록의 인덱스를 가져옵니다.
            const currentBlockIndex = this.editor.blocks.getCurrentBlockIndex();
            // 인덱스를 사용해 실제 블록 객체를 가져옵니다.
            const currentBlock = this.editor.blocks.getBlockByIndex(currentBlockIndex);

            if (currentBlock) {
                const blockId = currentBlock.id;

                // 미리보기 창에 정의된 새로운 함수(scrollToBlock)를 호출합니다.
                if (typeof this.previewWindow.scrollToBlock === 'function') {
                    this.previewWindow.scrollToBlock(blockId);
                }
            }
        } catch (error) {
            console.error("블록 정보를 가져오는 데 실패했습니다:", error);
        }
    }

    disableEditor(warningMessage) {
        if (this.hasOverlayTarget) {
            this.overlayTarget.innerHTML = warningMessage;
            this.overlayTarget.style.display = 'block'; // 오버레이 표시
        }
    }

    enableEditor() {
        if (this.hasOverlayTarget) {
            this.overlayTarget.innerHTML = '';
            this.overlayTarget.style.display = 'none'; // 오버레이 숨김
        }
    }

    autoSave() {
        if (this.sessionExpired) return;

        // collab 모드에서는 editor_data 저장을 Persister(Y.Doc 기반)가 담당.
        // autoSave()로 PATCH하면 stale 데이터로 덮어쓸 위험.
        // meta 필드(title 등)만 collab Y.Doc으로 동기화되므로 PATCH 불필요.
        if (this.collabSession) return;

        if (this.isDragging || this.alreadySaving || this.isTyping) {
            return;
        }

        if (this.isExceptionalEnvironment()) {
            return;
        }

        this.alreadySaving = true;
        this.editor.save().then((outputData) => {
            if (process.env.NODE_ENV === "development") {
                console.log(outputData.blocks)
            }

            // <--- 내용이 비어있고, 기존 post가 존재했다면 저장을 막는 로직 추가
            const isPostEmpty = !outputData.blocks || outputData.blocks.length === 0;
            const postExists = this.data.get("post-ext-id") !== "new";

            if (isPostEmpty && postExists) {
                // 기존에 내용이 있던 글이 갑자기 빈 내용으로 저장되려 할 때
                // 이것이 의도된 삭제인지, 오류인지 판단하기 어려우므로 일단 로그만 남기거나
                // 저장을 보류할 수 있다. (사용자가 모든 블록을 지운 경우도 있으므로 조심스럽게 접근)
                console.warn("Attempting to auto-save empty content over an existing post. Skipping.");
                this.alreadySaving = false;
                return; // 저장을 건너뛴다.
            }

            if (this.data.get("post-type") === "form") {
                this.editorDataTarget.value = JSON.stringify(outputData);
            } else {
                this.editorDataTarget.value = JSON.stringify(outputData);
                let params = {
                    commit: "auto_save"
                };
                params[this.data.get("param-key")] = {
                    title: this.titleTarget.value,
                    subtitle: this.subtitleTarget.value,
                    tag_list: this.tagListTarget.value,
                    post_category_slug: this.hasPostCategorySlugTarget && this.postCategorySlugTarget.value, // TODO template_draft 자동저장이랑 공유하는 로직. 수정필요.
                    editor_data: JSON.stringify(outputData)
                }

                api.patch(this.data.get("update-url"), params)
                    .then((res) => {
                        // res나 res.data가 undefined여도 오류가 발생하지 않도록 ?. 사용
                        const succeedMessage = res?.data?.succeed_message;
                        const warningMessage = res?.data?.warning_message;

                        // TODO template_draft 자동저장이랑 공유하는 로직. 수정필요.
                        if (this.hasAutoSavedLabelTarget) {
                            if (succeedMessage && succeedMessage.trim() !== "") {
                                this.autoSavedLabelTarget.classList.remove("hidden");
                                this.autoSavedLabelTarget.innerHTML = `${new Date().toLocaleTimeString()}<br>${succeedMessage}`;
                            } else {
                                this.autoSavedLabelTarget.classList.add("hidden");
                            }
                        }

                        if (this.hasWarningLabelTarget) {
                            if (warningMessage && warningMessage.trim() !== "") {
                                this.warningLabelTarget.classList.remove("hidden");
                                this.warningLabelTarget.innerHTML = warningMessage;
                            } else {
                                this.warningLabelTarget.classList.add("hidden");
                            }
                        }

                        if (res?.data?.abandoned_tab) {
                            this.disableEditor(warningMessage); // 에디터 비활성화
                        } else {
                            this.enableEditor(); // 에디터 활성화
                        }

                        if (this.previewWindow && !this.previewWindow.closed) {
                            try {
                                // 자식 창이 아직 로딩 중이면 refreshPreview가 없을 수 있으므로 확인 후 호출합니다.
                                if (typeof this.previewWindow.refreshPreview === 'function') {
                                    this.previewWindow.refreshPreview();
                                }
                            } catch (e) {
                                // 자식 창이 다른 도메인일 때 발생할 수 있는 cross-origin 오류를 방지합니다.
                                console.error("미리보기 창과 통신할 수 없습니다.", e);
                                this.previewWindow = null;
                            }
                        }

                    })
                    .catch((error) => {
                        console.error("자동 저장 실패:", error);
                        if (error.response && error.response.status === 401) {
                            this.handleSessionExpired();
                        } else {
                            this.showAutoSaveError();
                        }
                    });
            }

            this.alreadySaving = false;
        }).catch((e) => {
            console.log(e);
            this.alreadySaving = false;
        })
    }

    clearAndRenderDraft(editorData) {
        this.editor.blocks.clear();
        this.editor.blocks.render(editorData).then(() => {
            this.autoSave();
        });
    }

    clearAndRender(editorData) {
        this.editor.save().then((outputData) => {
            this.editor.blocks.clear();
            this.editor.blocks.render(editorData).then(() => {
                this.autoSave();
            });
        });
    }

    // 글박스(code 블록)를 column 블록으로 변환하는 함수
    // 기존 code 툴이 "회색 글박스" 용도로 사용되어 column 툴(1칼럼 + 배경색/패딩)로 대체
    migrateCodeBlockToColumn = (block) => {
        const codeText = block.data?.code || "";
        const paragraphs = this.parseCodeToParagraphs(codeText);

        return {
            type: "columns",
            id: block.id || this.generateBlockId(),
            data: {
                cols: [
                    {
                        blocks: paragraphs
                    }
                ],
                gap: 0,
                ratios: [1]
            },
            tunes: {
                blockStyle: {
                    backgroundColor: "#f9f8f7",
                    padding: { top: 8, right: 16, bottom: 8, left: 16 },
                    margin: { top: 10, right: 0, bottom: 10, left: 0 },
                    borderRadius: 3
                }
            }
        };
    }

    // code 텍스트를 paragraph 블록들로 파싱
    // 규칙: \n 1개 -> <br>, \n\n (빈 줄) -> 별도 paragraph 블록
    parseCodeToParagraphs = (codeText) => {
        if (!codeText || codeText.trim() === "") {
            return [this.createParagraphBlock("")];
        }

        // 연속 줄바꿈(\n\n 이상)을 기준으로 분리
        const sections = codeText.split(/\n{2,}/);

        return sections.map(section => {
            // 각 섹션 내의 단일 줄바꿈은 <br>로 변환
            const text = section.replace(/\n/g, "<br>");
            return this.createParagraphBlock(text);
        });
    }

    createParagraphBlock = (text) => {
        return {
            type: "paragraph",
            id: this.generateBlockId(),
            data: { text: text }
        };
    }

    generateBlockId = () => {
        return Math.random().toString(36).substring(2, 12);
    }

    // 글박스(TextBox) 블록 삽입 (CMD+SHIFT+B 단축키용)
    insertTextBox = () => {
        if (!this.editor) return;

        const currentIndex = this.editor.blocks.getCurrentBlockIndex();

        // 현재 블록을 교체 (Editor.js 기본 shortcut 동작과 동일)
        this.editor.blocks.insert('columns', {
            cols: [{ blocks: [] }],
            gap: 0,
            ratios: [1],
            _isTextBox: true
        }, undefined, currentIndex, true, true); // 마지막 true = replace
    }

    // 칼럼(Columns) 블록 삽입 (CMD+SHIFT+C 단축키용)
    insertColumns = () => {
        if (!this.editor) return;

        const currentIndex = this.editor.blocks.getCurrentBlockIndex();

        // 현재 블록을 교체 (Editor.js 기본 shortcut 동작과 동일)
        this.editor.blocks.insert('columns', {
            cols: [{ blocks: [] }, { blocks: [] }],
            gap: 10,
            ratios: [1, 1]
        }, undefined, currentIndex, true, true);
    }

    // 각주(Footnote) 삽입 (CMD+SHIFT+F 단축키용)
    insertFootnote = () => {
        if (!this.editor) return;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const editorEl = document.querySelector('.codex-editor');
        if (!editorEl || !editorEl.contains(range.startContainer)) return;

        FootnoteTool.insertAt(range);
    }

    // block = {id:, data: { items: [..], style: "unordered"  }, type:}
    legacyListToNestedList = (block) => {
        let blockData = block.data
        blockData.items = blockData.items.map((item) => {
            if (typeof(item) === "string") {
                return { content: item, items: [] };
            } else {
                return item;
            }
        })
        block.data = blockData;
        return block;
    }

    // IME composition 이벤트 핸들러 (arrow function으로 자동 바인딩)
    handleCompositionStart = () => {
        this.isComposing = true;
    }

    handleCompositionEnd = () => {
        this.isComposing = false;
    }

    async connect() {
        let editorJsController = this;
        let defaultData = {};

        try {
            if (this.data.get("post-type") === "form") {
                defaultData = JSON.parse(`${this.data.get("default-data")}`);
            } else {
                let response = await api.get(this.data.get("fetch-url"))
                const {
                    title,
                    subtitle,
                    editor_data,
                    tag_list,
                    post_category_slug
                } = response.data;

                this.titleTarget.value = title;
                this.subtitleTarget.value = subtitle;
                this.tagListTarget.value = tag_list;
                if (post_category_slug && post_category_slug.length > 0) {
                    this.postCategorySlugTarget.value = post_category_slug;
                }
                defaultData = editor_data;
            }
        } catch (error) {
            alert("콘텐츠를 불러오는 데 실패했습니다. 잠시 후 페이지를 새로고침 해주세요. 문제가 계속되면 고객센터로 문의해주세요.");
            // 에디터 초기화 로직을 아예 실행하지 않도록 여기서 함수를 종료
            return;
        }


        // editor_core.js의 buildTools + createEditorInstance를 사용
        const { tools, defaultBlockTunes } = buildTools({
            controller: this,
            lightMode: this.lightModeValue,
            includeAdvanced: true,
            excludeVote: false,
            excludeAiPolish: this.data.get("post-type") === "form",
            includePageWidgets: false,
            spaceUrl: this.data.get("space-url"),
            postExtId: this.data.get("post-ext-id"),
            currentPlan: this.currentPlanValue,
            primaryTextColor: this.primaryTextColorValue,
            primaryBackgroundColor: this.primaryBackgroundColorValue,
        });

        const holder = this.data.get("editor-container") || "editorContainer";

        const isCollabMode = this.data.get("collab-mode") === "true";

        this.editor = createEditorInstance({
            holder,
            data: defaultData,
            tools,
            tunes: defaultBlockTunes,
            lightMode: this.lightModeValue,
            collabMode: isCollabMode,
            onReady: ({ undo, holderElement }) => {
                this.undo = undo;
                this._setupNoteEditorEvents(holderElement);
                this._setupHeaderToc(holderElement);

                if (isCollabMode) {
                    this._initCollab(holderElement);
                }
            },
        });

        // Collab 모드에서는 서버가 저장하므로 클라이언트 autoSave 비활성화
        if (!isCollabMode) {
            let autoSavePeriod;

            if (this.data.get("post-type") === "form") {
                autoSavePeriod = 1000;
            } else {
                autoSavePeriod = process.env.NODE_ENV === 'production' ? 15000 : 10000000;
            }

            this.autoSaver = setInterval(() => {
                if (document.hidden === true) return;
                this.autoSave();
            }, autoSavePeriod);
        }

        // if (this.data.get("post-type") === "form") {
        //   this.autoSaver = setInterval(() => {
        //     if(document.hidden === true) return;
        //     this.autoSave();
        //   }, 1000);
        // }

        document.addEventListener("keydown", this.handleKeyDown);

        // IME composition 이벤트 리스너 추가
        document.addEventListener('compositionstart', this.handleCompositionStart);
        document.addEventListener('compositionend', this.handleCompositionEnd);

        // AI 채팅 콘텐츠 검토 이벤트 리스너
        document.addEventListener('editor:request-data', this.handleEditorDataRequest);
        document.addEventListener('editor:scroll-to-block', this.handleScrollToBlock);
        document.addEventListener('editor:apply-review-item', this.handleApplyReviewItem);
    }

    // AI 채팅에서 에디터 데이터 요청 핸들러
    handleEditorDataRequest = async () => {
        if (!this.editor) {
            document.dispatchEvent(new CustomEvent('editor:data-response', {
                detail: { data: null }
            }));
            return;
        }

        try {
            const data = await this.editor.save();
            document.dispatchEvent(new CustomEvent('editor:data-response', {
                detail: { data }
            }));
        } catch (error) {
            console.error('[EditorJS] 에디터 데이터 저장 실패:', error);
            document.dispatchEvent(new CustomEvent('editor:data-response', {
                detail: { data: null }
            }));
        }
    }

    // AI 채팅에서 특정 블록으로 스크롤 요청 핸들러
    handleScrollToBlock = (event) => {
        const { blockIndex, originalText } = event.detail;
        if (!this.editor || blockIndex === undefined) return;

        try {
            const block = this.editor.blocks.getBlockByIndex(blockIndex);
            if (!block) return;

            const blockElement = block.holder;
            if (!blockElement) return;

            // 1단계: 블록 테두리 하이라이트 (스크롤 전에 적용)
            blockElement.style.transition = 'box-shadow 0.3s ease';
            blockElement.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.5)';
            blockElement.style.borderRadius = '4px';

            // 2단계: scrollIntoView로 블록을 화면 중앙에 배치
            blockElement.scrollIntoView({ behavior: 'instant', block: 'center' });

            // 3단계: 스크롤 완료 후 포커스 및 텍스트 하이라이트
            // 블록에 포커스
            this.editor.caret.setToBlock(blockIndex, 'start');

            // originalText가 있으면 해당 텍스트를 찾아서 하이라이트
            if (originalText) {
                this.highlightTextInBlock(blockElement, originalText);
            }

            // 4단계: 블록 테두리 하이라이트 해제 (5초 후)
            setTimeout(() => {
                blockElement.style.boxShadow = '';
                blockElement.style.borderRadius = '';
            }, 5000);
        } catch (error) {
            console.error('[EditorJS] 블록 스크롤 실패:', error);
        }
    }

    // 모든 리뷰 하이라이트 제거
    clearAllReviewHighlights = () => {
        document.querySelectorAll('.ai-review-highlight').forEach(highlight => {
            this.unwrapHighlight(highlight);
        });
    }

    // 특정 하이라이트 제거
    unwrapHighlight = (highlight) => {
        if (highlight && highlight.parentNode) {
            const parent = highlight.parentNode;
            while (highlight.firstChild) {
                parent.insertBefore(highlight.firstChild, highlight);
            }
            parent.removeChild(highlight);
            parent.normalize();
        }
    }

    // 블록 내에서 특정 텍스트를 찾아 형광펜 하이라이트
    highlightTextInBlock = (blockElement, searchText) => {
        if (!searchText) return;

        // 해당 블록 내 기존 하이라이트 먼저 제거 (중첩 방지)
        blockElement.querySelectorAll('.ai-review-highlight').forEach(highlight => {
            this.unwrapHighlight(highlight);
        });

        // 고유 ID 생성
        const highlightId = `highlight-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

        // HTML 태그로 해석될 수 있는 <...>를 제거한 검색 텍스트 (fallback용)
        const normalizedSearchText = searchText.replace(/<[^>]*>/g, '');

        // 블록 내 편집 가능한 요소들 찾기
        const editableElements = blockElement.querySelectorAll('[contenteditable="true"]');

        for (const element of editableElements) {
            const textContent = element.textContent || '';
            // 원본 텍스트로 먼저 검색, 없으면 정규화된 텍스트로 검색
            let searchIndex = textContent.indexOf(searchText);
            let actualSearchText = searchText;
            if (searchIndex === -1 && normalizedSearchText !== searchText) {
                searchIndex = textContent.indexOf(normalizedSearchText);
                actualSearchText = normalizedSearchText;
            }

            if (searchIndex !== -1) {
                // 텍스트 노드 찾기
                const treeWalker = document.createTreeWalker(
                    element,
                    NodeFilter.SHOW_TEXT,
                    null,
                    false
                );

                let currentOffset = 0;
                let startNode = null;
                let startOffset = 0;
                let endNode = null;
                let endOffset = 0;

                while (treeWalker.nextNode()) {
                    const node = treeWalker.currentNode;
                    const nodeLength = node.textContent.length;

                    // 시작 노드 찾기
                    if (!startNode && currentOffset + nodeLength > searchIndex) {
                        startNode = node;
                        startOffset = searchIndex - currentOffset;
                    }

                    // 끝 노드 찾기
                    if (startNode && currentOffset + nodeLength >= searchIndex + actualSearchText.length) {
                        endNode = node;
                        endOffset = searchIndex + actualSearchText.length - currentOffset;
                        break;
                    }

                    currentOffset += nodeLength;
                }

                if (startNode && endNode) {
                    try {
                        const range = document.createRange();
                        range.setStart(startNode, startOffset);
                        range.setEnd(endNode, endOffset);

                        // 형광펜 마크 생성 (고유 ID 포함)
                        const highlight = document.createElement('mark');
                        highlight.className = 'ai-review-highlight';
                        highlight.dataset.highlightId = highlightId;
                        highlight.style.cssText = 'background-color: #fef08a; padding: 1px 2px; border-radius: 2px;';

                        // Range를 mark로 감싸기
                        range.surroundContents(highlight);

                        // 하이라이트 끝에 커서 위치
                        const selection = window.getSelection();
                        const cursorRange = document.createRange();
                        cursorRange.setStartAfter(highlight);
                        cursorRange.collapse(true);
                        selection.removeAllRanges();
                        selection.addRange(cursorRange);

                        // 5초 후 해당 하이라이트만 제거
                        setTimeout(() => {
                            const targetHighlight = document.querySelector(`[data-highlight-id="${highlightId}"]`);
                            this.unwrapHighlight(targetHighlight);
                        }, 5000);
                    } catch (e) {
                        // surroundContents 실패 시 (여러 노드에 걸친 경우) 무시
                        console.warn('[EditorJS] 하이라이트 마크 실패:', e);
                    }
                }

                return; // 첫 번째 매치에서 종료
            }
        }
    }

    // AI 채팅에서 검토 항목 적용 요청 핸들러
    handleApplyReviewItem = async (event) => {
        const { item } = event.detail;
        if (!this.editor || !item || !item.corrected_text) return;

        // 적용 전 하이라이트 제거
        this.clearAllReviewHighlights();

        try {
            const blockIndex = item.block_index;
            const block = this.editor.blocks.getBlockByIndex(blockIndex);

            if (!block) {
                console.warn(`[EditorJS] Block not found at index ${blockIndex}`);
                return;
            }

            // DOM에서 직접 텍스트 교체 (input 이벤트 없이)
            const blockElement = block.holder;
            const replaced = this.replaceTextInDOM(blockElement, item.original_text, item.corrected_text);

            if (!replaced) {
                console.warn('[EditorJS] 텍스트 교체 실패 - DOM에서 텍스트를 찾을 수 없음');
            }

            // 자동 저장 주기에 의해 저장됨 (즉시 저장하지 않음)
            // input 이벤트를 발생시키지 않으므로 EditorJS 내부 상태 변경 없음
            // columns 블록의 경우 save() 시 내부 에디터가 DOM에서 직접 읽음
        } catch (error) {
            console.error('[EditorJS] 검토 항목 적용 실패:', error);
        }
    }

    // DOM에서 직접 텍스트 교체 (input 이벤트 없이, tunes 보존을 위해)
    replaceTextInDOM(blockElement, original, replacement) {
        if (!blockElement) return false;

        const editableElements = blockElement.querySelectorAll('[contenteditable="true"]');
        const normalizeWhitespace = (str) => str.replace(/\s+/g, ' ').trim();
        const normalizedOriginal = normalizeWhitespace(original);

        for (const element of editableElements) {
            const textContent = element.textContent || '';
            const normalizedContent = normalizeWhitespace(textContent);

            if (normalizedContent.includes(normalizedOriginal)) {
                // innerHTML에서 교체 시도
                const escapedOriginal = original.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const flexiblePattern = escapedOriginal.replace(/\s+/g, '\\s+');
                const regex = new RegExp(flexiblePattern, 'g');

                if (regex.test(element.innerHTML)) {
                    element.innerHTML = element.innerHTML.replace(new RegExp(flexiblePattern), replacement);
                    // input 이벤트를 발생시키지 않음 - tunes 보존을 위해
                    // 변경사항은 auto-save 시 block.save()에서 DOM에서 읽어감
                    return true;
                }

                // textContent로 직접 교체 시도 (HTML 태그 없는 경우)
                if (element.innerHTML === element.textContent) {
                    element.textContent = textContent.replace(new RegExp(flexiblePattern), replacement);
                    // input 이벤트를 발생시키지 않음
                    return true;
                }
            }
        }

        return false;
    }

    // 블록 데이터 내 텍스트 교체
    replaceTextInBlockData(data, original, replacement) {
        if (!data || typeof data !== 'object') return data;

        const result = Array.isArray(data) ? [...data] : { ...data };

        // 공백 정규화 함수 (줄바꿈, 탭 등을 단일 공백으로)
        const normalizeWhitespace = (str) => str.replace(/\s+/g, ' ').trim();

        for (const key in result) {
            if (typeof result[key] === 'string') {
                const currentValue = result[key];
                const normalizedCurrent = normalizeWhitespace(currentValue);
                const normalizedOriginal = normalizeWhitespace(original);

                // 정규화된 텍스트로 매칭 확인
                if (normalizedCurrent.includes(normalizedOriginal)) {
                    // 실제 교체: 원본의 공백 패턴을 유연하게 매칭
                    const escapedOriginal = original.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const flexiblePattern = escapedOriginal.replace(/\\s\+/g, '\\s+').replace(/\s+/g, '\\s+');
                    const regex = new RegExp(flexiblePattern);
                    result[key] = currentValue.replace(regex, replacement);
                } else {
                    // 기존 방식으로 시도 (정확한 매칭)
                    result[key] = currentValue.replace(original, replacement);
                }
            } else if (typeof result[key] === 'object' && result[key] !== null) {
                result[key] = this.replaceTextInBlockData(result[key], original, replacement);
            }
        }

        return result;
    }

    // 각주 Backspace 삭제: 커서가 미주 바로 뒤에 있을 때 미주를 삭제합니다.
    _handleFootnoteBackspace = (e) => {
        const sel = window.getSelection();
        if (!sel || !sel.isCollapsed || sel.rangeCount === 0) return;

        const range = sel.getRangeAt(0);
        const { startContainer, startOffset } = range;
        let supToDelete = null;

        // Case 1: 텍스트 노드 안에서 offset ≤ 1이고 앞 형제가 sup[data-footnote]
        //         (zero-width space 안이거나 바로 뒤)
        if (startContainer.nodeType === Node.TEXT_NODE && startOffset <= 1) {
            const prev = startContainer.previousSibling;
            if (prev?.tagName === 'SUP' && prev.hasAttribute('data-footnote')) {
                supToDelete = prev;
            }
        }

        // Case 2: 엘리먼트 노드에서 이전 자식이 sup[data-footnote]
        if (!supToDelete && startContainer.nodeType === Node.ELEMENT_NODE && startOffset > 0) {
            const prev = startContainer.childNodes[startOffset - 1];
            if (prev?.tagName === 'SUP' && prev.hasAttribute('data-footnote')) {
                supToDelete = prev;
            }
            // Case 3: 이전 자식이 ZWS 텍스트 노드이고 그 앞이 sup
            if (!supToDelete && prev?.nodeType === Node.TEXT_NODE && prev.textContent === '\u200B') {
                const prevPrev = prev.previousSibling;
                if (prevPrev?.tagName === 'SUP' && prevPrev.hasAttribute('data-footnote')) {
                    supToDelete = prevPrev;
                    prev.remove(); // ZWS도 함께 제거
                }
            }
        }

        if (supToDelete) {
            e.preventDefault();
            // sup 뒤의 ZWS가 있으면 함께 제거
            const next = supToDelete.nextSibling;
            if (next?.nodeType === Node.TEXT_NODE && next.textContent.startsWith('\u200B')) {
                next.textContent = next.textContent.substring(1);
                if (!next.textContent) next.remove();
            }
            supToDelete.remove();
            refreshFootnotePanel();
        }
    }

    handleSessionExpired(reason) {
        if (this.sessionExpired) return;
        this.sessionExpired = true;

        clearInterval(this.autoSaver);

        // collab takeover는 다른 기기/탭에서 같은 Editor 계정으로 접속한 경우.
        // 저장 불가 사유와 복구 방법이 다르므로 문구를 분기한다.
        const message = reason === 'collab_takeover'
            ? "다른 기기 또는 탭에서 편집을 시작했습니다.<br>여기에서 이어서 편집하려면 페이지를 새로고침해주세요."
            : "세션이 만료되어 저장할 수 없습니다.<br>다른 탭에서 로그인한 후 이 페이지로 돌아와 내용을 복사해주세요.";

        this.disableEditor(message);
    }

    // ✨ 키보드 이벤트를 처리하는 통합 핸들러
    handleKeyDown = (e) => {
        const isCmdOrCtrl = window.navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey;

        if (isCmdOrCtrl) {
            switch (e.key.toLowerCase()) {
                case 's': // 저장 (Cmd/Ctrl + S)
                    e.preventDefault();
                    this.autoSave();
                    break;
                case 'b': // 글박스 (Cmd/Ctrl + Shift + B)
                    if (e.shiftKey && this.editor) {
                        e.preventDefault();
                        this.insertTextBox();
                    }
                    break;
                case 'c': // 칼럼 (Cmd/Ctrl + Shift + C)
                    if (e.shiftKey && this.editor) {
                        e.preventDefault();
                        this.insertColumns();
                    }
                    break;
                case 'f': // 각주 (Cmd/Ctrl + Shift + F)
                    if (e.shiftKey && this.editor) {
                        e.preventDefault();
                        this.insertFootnote();
                    }
                    break;
            }
        }

        // Safari에서만 Shift+Enter 처리 (IME 입력 중이 아닐 때)
        if (this.isSafari && e.shiftKey && e.key === 'Enter' && !this.isComposing) {
            // 현재 포커스된 요소가 paragraph 블록인지 확인
            const activeElement = document.activeElement;
            if (activeElement && activeElement.closest('.ce-paragraph')) {
                e.preventDefault();

                // 대신 일반 Enter 키 이벤트를 발생시킴
                const enterEvent = new KeyboardEvent('keydown', {
                    key: 'Enter',
                    code: 'Enter',
                    keyCode: 13,
                    which: 13,
                    bubbles: true,
                    cancelable: true
                });
                activeElement.dispatchEvent(enterEvent);
            }
        }
    };

    // Note 에디터 전용 이벤트 설정 (createEditorInstance onReady에서 호출)
    _setupNoteEditorEvents(holderElement) {
        // 드래그 상태 관리
        const resetDragState = () => {
            clearTimeout(this.dragResetTimer);
            this.dragResetTimer = setTimeout(() => { this.isDragging = false; }, 500);
        };

        holderElement.addEventListener('dragstart', () => {
            this.isDragging = true;
            clearTimeout(this.dragResetTimer);
            this.dragResetTimer = setTimeout(() => { this.isDragging = false; }, 5000);
        });
        holderElement.addEventListener('drop', resetDragState);
        holderElement.addEventListener('dragend', resetDragState);
        holderElement.addEventListener('dragleave', (e) => {
            if (e.target === holderElement && !holderElement.contains(e.relatedTarget)) resetDragState();
        });

        // 키보드: 드래그 해제 + 타이핑 상태 + 각주 Backspace
        holderElement.addEventListener('keydown', (e) => {
            if (this.isDragging) { this.isDragging = false; clearTimeout(this.dragResetTimer); }
            if (['Enter', 'Delete', 'Backspace'].includes(e.key)) {
                this.isTyping = true;
                clearTimeout(this.typingTimer);
                this.typingTimer = setTimeout(() => { this.isTyping = false; }, 500);
            }
            if (e.key === 'Backspace') this._handleFootnoteBackspace(e);
        });

        // 프리뷰 동기화
        holderElement.addEventListener('click', () => this.sendCurrentBlockIdToPreview());
        holderElement.addEventListener('keyup', (e) => {
            if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown'].includes(e.key)) {
                setTimeout(() => this.sendCurrentBlockIdToPreview(), 100);
            }
        });

        // 세션 만료
        this.handleSessionExpiredBound = (e) => this.handleSessionExpired(e?.detail?.reason);
        document.addEventListener('session:expired', this.handleSessionExpiredBound);

        if (this.hasPreviewButtonTarget) this.previewButtonTarget.disabled = false;
    }

    // 헤더 목차(어코디언) — 상세 header_spy 와 동일한 로직을 에디터 지면에 동적으로 표시
    _setupHeaderToc(holderElement) {
        if (!this.hasTocTarget) return;

        this.headerToc = new EditorHeaderToc({
            editor: this.editor,
            holderElement,
            container: this.tocTarget,
            maxLevel: 3,
        });
        this.headerToc.start();
    }

    _initCollab(holderElement) {
        const recordType = this.data.get("collab-record-type") || "Post";
        const recordId = parseInt(this.data.get("collab-record-id"), 10);
        const editorId = this.data.get("collab-editor-id") || null;
        const userName = this.data.get("collab-user-name") || "Anonymous";
        const userImage = this.data.get("collab-user-image") || null;

        // Nested editor 참조 저장 (columns 내부 깜빡임 없는 render용)
        this._collabNestedEditors = new Map();

        // Presence panel — view의 target 요소에 삽입
        const presenceContainer = this.hasPresencePanelTarget
            ? this.presencePanelTarget
            : holderElement.parentElement;

        this.presencePanel = new PresencePanel({
            container: presenceContainer,
            self: { name: userName, image: userImage },
        });

        // Collab session
        this.collabSession = new CollabSession({
            editor: this.editor,
            recordType,
            recordId,
            editorId,
            userName,
            userImage,
            nestedEditorRegistry: this._collabNestedEditors,
            onSynced: () => {
                // 초기 sync 완료 — awareness cursor 활성화
                this.awarenessCursor = new AwarenessCursor({
                    awareness: this.collabSession.awareness,
                    doc: this.collabSession.doc,
                    editor: this.editor,
                    holderElement,
                });

                // binding에 awarenessCursor 연결 (텍스트 동기화 후 caret 갱신용)
                if (this.collabSession.binding) {
                    this.collabSession.binding.awarenessCursor = this.awarenessCursor;
                }

                // Meta fields 양방향 동기화 (title, subtitle, tagList, category)
                this.metaBinding = new MetaBinding({
                    yMeta: this.collabSession.yMeta,
                    doc: this.collabSession.doc,
                    awareness: this.collabSession.awareness,
                    provider: this.collabSession.provider,
                    fields: {
                        title: this.hasTitleTarget ? this.titleTarget : null,
                        subtitle: this.hasSubtitleTarget ? this.subtitleTarget : null,
                        tagList: this.hasTagListTarget ? this.tagListTarget : null,
                        postCategorySlug: this.hasPostCategorySlugTarget ? this.postCategorySlugTarget : null,
                    },
                    onBlur: () => this.autoSave(),
                });
            },
            onStatus: (status) => {
                this.presencePanel?.setStatus(status);
            },
            onPresence: (data) => {
                this.presencePanel?.handlePresenceEvent(data);
            },
            onConnected: (data) => {
                // 실제 connection_uid를 받으면 자기 자신 key 업데이트
                this.presencePanel?.updateMyConnectionUid(data.connection_uid);
            },
        });

        this.collabSession.connect();

        // Cmd+Z / Cmd+Shift+Z를 collab undo로 라우팅.
        // 리스너는 disconnect()에서 제거되지 않으면 Turbo 캐시/재연결 시 stale this를 참조해
        // null.undo() 오류가 난다 — holderElement 참조와 함께 보관하여 disconnect에서 정리.
        this._collabKeydownHandler = (e) => {
            if (!this.collabSession) return; // disconnect 후 stale 리스너 방어
            if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
                e.preventDefault();
                e.stopPropagation();
                if (e.shiftKey) {
                    this.collabSession.redo();
                } else {
                    this.collabSession.undo();
                }
            }
        };
        this._collabKeydownHolder = holderElement;
        holderElement.addEventListener('keydown', this._collabKeydownHandler, true);
    }

    disconnect() {
        // 컨트롤러가 페이지에서 사라질 때, 열려있던 팝업창도 함께 닫아줍니다. (선택사항이지만 좋은 UX)
        if (this.previewWindow && !this.previewWindow.closed) {
            this.previewWindow.close();
        }

        window.connectPreview = null;

        if (this.autoSaver) {
            clearInterval(this.autoSaver);
        }

        if (this.dragResetTimer) {
            clearTimeout(this.dragResetTimer);
        }

        if (this.typingTimer) {
            clearTimeout(this.typingTimer);
        }

        // Paragraph Transformer cleanup 호출
        if (this.paragraphTransformerCleanup && typeof this.paragraphTransformerCleanup === 'function') {
            try {
                this.paragraphTransformerCleanup();
            } catch (error) {
                console.error('[EditorJS] Paragraph Transformer cleanup 실패:', error);
            }
            this.paragraphTransformerCleanup = null;
        }

        // 각주 패널 cleanup
        if (this.footnoteCleanup && typeof this.footnoteCleanup === 'function') {
            this.footnoteCleanup();
            this.footnoteCleanup = null;
        }

        // 헤더 목차 cleanup
        if (this.headerToc) {
            this.headerToc.destroy();
            this.headerToc = null;
        }

        document.removeEventListener("keydown", this.handleKeyDown); // ✨ 통합 핸들러 제거

        // composition 이벤트 리스너 제거 (바인딩된 함수 참조 사용)
        document.removeEventListener('compositionstart', this.handleCompositionStart);
        document.removeEventListener('compositionend', this.handleCompositionEnd);

        // 세션 만료 이벤트 리스너 제거
        if (this.handleSessionExpiredBound) {
            document.removeEventListener('session:expired', this.handleSessionExpiredBound);
        }

        // AI 채팅 콘텐츠 검토 이벤트 리스너 제거
        document.removeEventListener('editor:request-data', this.handleEditorDataRequest);
        document.removeEventListener('editor:scroll-to-block', this.handleScrollToBlock);
        document.removeEventListener('editor:apply-review-item', this.handleApplyReviewItem);

        // Collab cleanup
        this._collabNestedEditors = null;
        if (this._collabKeydownHandler && this._collabKeydownHolder) {
            this._collabKeydownHolder.removeEventListener('keydown', this._collabKeydownHandler, true);
            this._collabKeydownHandler = null;
            this._collabKeydownHolder = null;
        }
        if (this.metaBinding) {
            this.metaBinding.destroy();
            this.metaBinding = null;
        }
        if (this.collabSession) {
            this.collabSession.destroy();
            this.collabSession = null;
        }
        if (this.awarenessCursor) {
            this.awarenessCursor.destroy();
            this.awarenessCursor = null;
        }
        if (this.presencePanel) {
            this.presencePanel.destroy();
            this.presencePanel = null;
        }

        if (this.editor) {
            this.editor.isReady.then(() => {
                this.editor.destroy();
                this.editor = null; // 참조 제거
            });
        }
    }
}